import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { api } from '../utils/api';
import { formatDate, formatAmount } from '../utils/helpers';
import { useAuth } from '../context/AuthContext';
import { IconTrash } from '../components/Icons';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Projects (the project list is read by the Header via the context, so
  // this page only needs the selected ID; the full list isn't kept here).
  const [, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);

  // Receipts
  const [receipts, setReceipts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  // Page size — persisted so the user's choice sticks across reloads.
  // Backend caps at 100, so 10/50/100 is a safe range.
  const PAGE_SIZE_OPTIONS = [10, 50, 100];
  const initialLimit = (() => {
    try {
      const raw = parseInt(localStorage.getItem('dashboardPageSize') || '', 10);
      return PAGE_SIZE_OPTIONS.includes(raw) ? raw : 10;
    } catch (_) { return 10; }
  })();
  const [limit, setLimit] = useState(initialLimit);

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1); // jump back to first page so we don't end up past the end
    try { localStorage.setItem('dashboardPageSize', String(newLimit)); } catch (_) {}
  };
  
  // Filters
  const [sort, setSort] = useState('date_desc');
  const [filter, setFilter] = useState('all');
  const [filterId, setFilterId] = useState('');
  const [filterConcept, setFilterConcept] = useState('');
  const [filterPayer, setFilterPayer] = useState('');
  const [filterRecipient, setFilterRecipient] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Stats
  const [cashCount, setCashCount] = useState(0);
  const [electronicCount, setElectronicCount] = useState(0);
  const [totals, setTotals] = useState({ income: 0, withdrawals: 0, net: 0 });
  
  // Modals
  const [showModal, setShowModal] = useState(false);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showConceptModal, setShowConceptModal] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  
  // Quote form data
  const [quoteData, setQuoteData] = useState({
    quote_number: '',
    quote_type: '',
    client: '',
    provider: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    description: ''
  });
  const [quotePhotos, setQuotePhotos] = useState([]);
  const [quotePhotoFiles, setQuotePhotoFiles] = useState([]);
  const [quoteGrayscale, setQuoteGrayscale] = useState(false);
  const [quoteCompression, setQuoteCompression] = useState(0.8);
  const [quoteSubmitting, setQuoteSubmitting] = useState(false);
  
  // Crop state
  const [showCropModal, setShowCropModal] = useState(false);
  const [cropImageIndex, setCropImageIndex] = useState(null);
  const [crop, setCrop] = useState({ unit: '%', width: 50, aspect: undefined });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [quoteCropData, setQuoteCropData] = useState({}); // Store crop data per image index
  const imgRef = useRef(null);

  // Per-row email-send state. sendingEmailId tracks which row is mid-
  // send so we can show a spinner on the right button. rowMessages
  // holds the last status message keyed by receipt ID — it briefly
  // surfaces success/failure under the actions, then auto-clears.
  const [sendingEmailId, setSendingEmailId] = useState(null);
  const [rowMessages, setRowMessages] = useState({}); // { [receiptId]: { kind: 'ok'|'err', text: string } }
  const rowMessageTimers = useRef({}); // { [receiptId]: timeoutId }

  // Form data
  const [cancelReceiptId, setCancelReceiptId] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [newPayer, setNewPayer] = useState('');
  const [newRecipient, setNewRecipient] = useState('');
  const [newConcept, setNewConcept] = useState('');
  // Withdrawal-modal "type new..." companion inputs (mirror of the receipt
  // modal's newPayer/newRecipient/newConcept). Cleared when the modal closes.
  const [newWFrom, setNewWFrom] = useState('');
  const [newWTo, setNewWTo] = useState('');
  const [newWConcept, setNewWConcept] = useState('');
  const [formData, setFormData] = useState({
    amount: '', payer: '', recipient: '', date: new Date().toISOString().slice(0, 16),
    concept: '', payment_method: 'cash', type: 'income',
  });
  const [withdrawalData, setWithdrawalData] = useState({
    amount: '', payer: '', recipient: '', date: new Date().toISOString().slice(0, 16),
    concept: '', payment_method: 'cash', type: 'withdrawal', description: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  
  // Export
  const [reportType, setReportType] = useState('regular');
  const [concepts, setConcepts] = useState([]);
  const [selectedConcepts, setSelectedConcepts] = useState([]);
  const [conceptPage, setConceptPage] = useState(1);
  const conceptsPerPage = 10;

  // Load projects on mount
  useEffect(() => {
    const loadProjects = async () => {
      try {
        const data = await api.getMyProjects();
        setProjects(data);
        const saved = localStorage.getItem('selectedProjectId');
        if (saved) {
          setSelectedProjectId(parseInt(saved));
        } else if (data.length > 0) {
          setSelectedProjectId(data[0].id);
          localStorage.setItem('selectedProjectId', data[0].id);
        }
      } catch (err) {
        console.error('Failed to fetch projects:', err);
        setError('Failed to load projects. Please refresh.');
      }
    };
    loadProjects();
  }, []);

  // Fetch receipts and contacts when filters change
  useEffect(() => {
    if (selectedProjectId) {
      fetchReceipts();
      fetchContacts();
      }
  // fetchReceipts and fetchContacts intentionally read the latest state via
  // closures, and the dep list above covers every value they consume. The
  // exhaustive-deps rule would require us to wrap them in useCallback,
  // which adds noise without changing behavior here.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, sort, filter, filterId, filterConcept, filterPayer, filterRecipient, dateFrom, dateTo, selectedProjectId]);

  const fetchReceipts = async () => {
    if (!selectedProjectId) return;
    setLoading(true);
    try {
      const data = await api.getReceipts(page, limit, sort, filter, filterId, filterConcept, filterPayer, filterRecipient, dateFrom, dateTo, selectedProjectId);
      setReceipts(data.receipts);
      setTotal(data.total);
      setCashCount(data.cash_count || 0);
      setElectronicCount(data.electronic_count || 0);
      setTotals({ income: data.total_income || 0, withdrawals: data.total_withdrawals || 0, net: data.net_total || 0 });
    } catch (err) {
      console.error('Failed to fetch receipts:', err);
      setError('Failed to load receipts. Please refresh.');
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async () => {
    try {
      const data = await api.getContacts(selectedProjectId);
      setContacts(data);
      const uniqueConcepts = [...new Set(data.filter(c => c.type === 'concept').map(c => c.name))];
      setConcepts(uniqueConcepts);
      setSelectedConcepts(uniqueConcepts);
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
      setError('Failed to load contacts. Please refresh.');
    }
  };

  // Helper: Auto-create new contacts (payer/recipient/concept)
  const ensureContactsExist = async (data) => {
    const existingPayers = (contacts || []).filter(c => c.type === 'sender').map(c => c.name.toLowerCase());
    const existingRecipients = (contacts || []).filter(c => c.type === 'recipient').map(c => c.name.toLowerCase());
    const existingConcepts = (contacts || []).filter(c => c.type === 'concept').map(c => c.name.toLowerCase());

    if (data.payer && !existingPayers.includes(data.payer.toLowerCase())) {
      try { await api.createContact(data.payer, 'sender', selectedProjectId); } catch (e) { console.error('Failed to create payer contact:', e); }
    }
    if (data.recipient && !existingRecipients.includes(data.recipient.toLowerCase())) {
      try { await api.createContact(data.recipient, 'recipient', selectedProjectId); } catch (e) { console.error('Failed to create recipient contact:', e); }
    }
    if (data.concept && !existingConcepts.includes(data.concept.toLowerCase())) {
      try { await api.createContact(data.concept, 'concept', selectedProjectId); } catch (e) { console.error('Failed to create concept contact:', e); }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await ensureContactsExist(formData);
      await api.createReceipt({ ...formData, amount: Math.round(parseFloat(formData.amount) * 100) / 100 }, selectedProjectId);
      setShowModal(false);
      setFormData({ amount: '', payer: '', recipient: '', date: new Date().toISOString().slice(0, 16), concept: '', payment_method: 'cash' });
      setNewPayer(''); setNewRecipient(''); setNewConcept('');
      fetchReceipts();
      fetchContacts();
    } catch (err) {
      console.error('Failed to create receipt:', err);
      setError(err.message || 'Failed to create receipt. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitWithdrawal = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await ensureContactsExist(withdrawalData);
      await api.createReceipt({ ...withdrawalData, amount: Math.round(parseFloat(withdrawalData.amount) * 100) / 100, type: 'withdrawal' }, selectedProjectId);
      setShowWithdrawalModal(false);
      setWithdrawalData({ amount: '', payer: '', recipient: '', date: new Date().toISOString().slice(0, 16), concept: '', payment_method: 'cash', type: 'withdrawal', description: '' });
      setNewWFrom(''); setNewWTo(''); setNewWConcept('');
      fetchReceipts();
      fetchContacts();
    } catch (err) {
      console.error('Failed to create withdrawal:', err);
      setError(err.message || 'Failed to create withdrawal. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitQuote = async (e) => {
    e.preventDefault();
    setError('');
    setQuoteSubmitting(true);
    try {
      const quotePayload = {
        ...quoteData,
        amount: Math.round(parseFloat(quoteData.amount) * 100) / 100,
        project_id: selectedProjectId
      };
      const result = await api.createQuote(quotePayload, selectedProjectId);
      
      // Upload photos if any
      if (quotePhotoFiles.length > 0 && result.uuid) {
        await processAndUploadQuotePhotos(result.uuid);
      }
      
      setShowQuoteModal(false);
      setQuoteData({ quote_number: '', quote_type: '', client: '', provider: '', amount: '', date: new Date().toISOString().split('T')[0], description: '' });
      setQuotePhotos([]);
      setQuotePhotoFiles([]);
      setQuoteCropData({});
      alert('Quote created successfully!');
    } catch (err) {
      console.error('Failed to create quote:', err);
      setError(err.message || 'Failed to create quote. Please try again.');
    } finally {
      setQuoteSubmitting(false);
    }
  };

  const processAndUploadQuotePhotos = async (quoteUuid) => {
    if (quotePhotoFiles.length === 0) return;
    
    const token = localStorage.getItem('token');
    const uploadDir = '/api/quotes/' + quoteUuid + '/photo';
    
    // Process each image with canvas
    for (let i = 0; i < quotePhotoFiles.length; i++) {
      const file = quotePhotoFiles[i];
      const cropData = quoteCropData[i];
      const processedBlob = await processImage(file, quoteGrayscale, quoteCompression, cropData);
      
      const formData = new FormData();
      formData.append('photos', processedBlob, file.name.replace(/\.[^.]+$/, '.jpg'));
      
      const response = await fetch(uploadDir, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error('Failed to upload photo');
      }
    }
  };

  const processImage = (file, grayscale, quality, cropData = null) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let sx = 0, sy = 0, sWidth = img.width, sHeight = img.height;
        
        // Apply crop if provided
        if (cropData && cropData.width && cropData.height) {
          sx = cropData.x;
          sy = cropData.y;
          sWidth = cropData.width;
          sHeight = cropData.height;
        }
        
        canvas.width = sWidth;
        canvas.height = sHeight;
        const ctx = canvas.getContext('2d');
        
        if (grayscale) {
          ctx.filter = 'grayscale(100%)';
        }
        ctx.drawImage(img, sx, sy, sWidth, sHeight, 0, 0, sWidth, sHeight);
        
        canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality);
      };
      img.src = URL.createObjectURL(file);
    });
  };


  const handleCancel = (id) => { setCancelReceiptId(id); setCancelReason(''); setShowCancelModal(true); };

  // Send the receipt PDF by email directly from the listing page. Same
  // backend as the detail page's "Enviar por Email" button — the
  // Spanish template, the QR-coded PDF, and the SMTP credentials in
  // Admin → Delivery are unchanged. We keep the feedback inline (under
  // the row's actions) instead of an alert() so the operator can see
  // it while staying on the list.
  const handleSendEmail = async (receipt) => {
    // Use the same resolver as the UI visibility check so the button
    // is honest: if the resolver says we don't have an email (no
    // snapshot AND no contact match), the click should also fail
    // loudly instead of hitting the backend and getting a 400 back.
    const resolved = resolveReceiptEmail(receipt);
    if (!resolved) {
      setRowMessages(prev => ({ ...prev, [receipt.id]: { kind: 'err', text: '✗ Sin email del pagador' } }));
      scheduleRowMessageClear(receipt.id);
      return;
    }
    setSendingEmailId(receipt.id);
    setRowMessages(prev => ({ ...prev, [receipt.id]: { kind: 'ok', text: 'Enviando…' } }));
    try {
      const r = await api.sendReceiptEmail(receipt.id);
      setRowMessages(prev => ({ ...prev, [receipt.id]: { kind: 'ok', text: r.message || '✓ Email enviado' } }));
    } catch (err) {
      setRowMessages(prev => ({ ...prev, [receipt.id]: { kind: 'err', text: '✗ ' + (err.message || 'error') } }));
    } finally {
      setSendingEmailId(null);
      scheduleRowMessageClear(receipt.id);
    }
  };

  // Auto-clear the per-row email status after 6s. We track timers in a
  // ref so re-clicks on the same row reset the prior countdown.
  const scheduleRowMessageClear = (id) => {
    if (rowMessageTimers.current[id]) {
      clearTimeout(rowMessageTimers.current[id]);
    }
    rowMessageTimers.current[id] = setTimeout(() => {
      setRowMessages(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      delete rowMessageTimers.current[id];
    }, 6000);
  };

  // Admin-only hard delete. Confirmation modal reuses the
  // typed-receipt-ID safety check from the detail page; on success
  // the list is re-fetched so the row disappears.
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const openDeleteModal = (id) => {
    setDeleteTargetId(id);
    setDeleteConfirmText('');
    setDeleteError('');
  };
  const closeDeleteModal = () => {
    if (deleting) return;
    setDeleteTargetId(null);
    setDeleteConfirmText('');
    setDeleteError('');
  };
  const handleDelete = async () => {
    if (deleteConfirmText !== String(deleteTargetId)) {
      setDeleteError(`Type the receipt ID (${deleteTargetId}) to confirm.`);
      return;
    }
    setDeleting(true);
    setDeleteError('');
    try {
      await api.deleteReceipt(deleteTargetId);
      setDeleteTargetId(null);
      setDeleteConfirmText('');
      fetchReceipts();
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete receipt');
      setDeleting(false);
    }
  };

  const handleConfirmCancel = async () => {
    if (!cancelReason.trim()) { alert('Please provide a reason'); return; }
    try {
      await api.cancelReceipt(cancelReceiptId, cancelReason);
      setShowCancelModal(false);
      setCancelReceiptId(null);
      setCancelReason('');
      fetchReceipts();
    } catch (err) { alert(err.message); }
  };

  const handleProceedExport = async () => {
    setShowExportModal(false);
    if (reportType === 'pooled') { setConceptPage(1); setShowConceptModal(true); return; }
    try {
      const text = await api.exportAllReceipts(filter, filterId, filterConcept, filterPayer, filterRecipient, dateFrom, dateTo, selectedProjectId);
      const printWindow = window.open('', '_blank');
      printWindow.document.write(text);
      printWindow.document.close();
      setTimeout(() => printWindow.print(), 500);
    } catch (err) { alert('Export failed: ' + err.message); }
  };

  const handlePooledExport = async () => {
    if (selectedConcepts.length === 0) { alert('Select at least one concept'); return; }
    setShowConceptModal(false);
    try {
      const text = await api.exportPooledReceipts(filter, filterId, dateFrom, dateTo, selectedConcepts, selectedProjectId);
      const printWindow = window.open('', '_blank');
      printWindow.document.write(text);
      printWindow.document.close();
      setTimeout(() => printWindow.print(), 500);
    } catch (err) { alert('Export failed: ' + err.message); }
  };

  const totalPages = Math.ceil(total / limit);
  const contactsList = contacts || [];

  // build a payer-name → contact.email map so the UI can show the
  // 📧 Email badge + button even when a receipt was created before
  // the contact-edit flow landed (commit d0cdf730, 2026-07-18) and
  // therefore has no payer_contact_id. We prefer contacts of type
  // "sender" (the payer role) so a payer doesn't accidentally
  // resolve to a "concept" entry of the same name.
  const senderEmailByName = {};
  for (const c of contactsList) {
    if (c.type === 'sender' && c.email) {
      senderEmailByName[(c.name || '').trim().toLowerCase()] = c.email;
    }
  }
  // Return the email we'll actually send to, preferring the
  // receipt's own snapshot. Mirrors handlers.ResolveEmailViaDB on
  // the backend so the button visibility matches what the handler
  // would use at send-time.
  const resolveReceiptEmail = (receipt) =>
    (receipt && receipt.payer_email) ? receipt.payer_email :
    ((receipt && receipt.payer)
        ? (senderEmailByName[(receipt.payer || '').trim().toLowerCase()] || '')
        : '');

  return (
    <div>
      <div className="dashboard-header">
        <h1>Receipts</h1>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <button onClick={() => setShowExportModal(true)} className="btn btn-outline">Export All</button>
          <button onClick={() => setShowModal(true)} className="btn btn-primary">+ New Receipt</button>
          <button onClick={() => setShowWithdrawalModal(true)} className="btn btn-warning">+ New Withdrawal</button>
        </div>
      </div>

      <div className="dashboard-stats">
        <div className="stat-card"><h3>Total Receipts</h3><div className="value">{total}</div></div>
        <div className="stat-card"><h3>Net Total</h3><div className="value">{formatAmount(totals.net)}</div></div>
        <div className="stat-card"><h3>Cash</h3><div className="value">{cashCount}</div></div>
        <div className="stat-card"><h3>Electronic</h3><div className="value">{electronicCount}</div></div>
      </div>

      <div className="receipts-container">
        <div className="receipts-header">
          <div className="header-left"><h2>All Receipts</h2><p className="sort-hint">Click headers to sort</p></div>
          <div className="filter-controls">
            <select value={filter} onChange={(e) => { setFilter(e.target.value); setPage(1); }}>
              <option value="all">All</option><option value="active">Active</option><option value="cancelled">Cancelled</option>
              <option value="income">Income</option><option value="withdrawals">Withdrawals</option>
            </select>
            <select value={filterConcept} onChange={(e) => { setFilterConcept(e.target.value); setPage(1); }}>
              <option value="">All Concepts</option>
              {contactsList.filter(c => c.type === 'concept').map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
            <select value={filterPayer} onChange={(e) => { setFilterPayer(e.target.value); setPage(1); }}>
              <option value="">All Payers</option>
              {contactsList.filter(c => c.type === 'sender').map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
            <select value={filterRecipient} onChange={(e) => { setFilterRecipient(e.target.value); setPage(1); }}>
              <option value="">All Recipients</option>
              {contactsList.filter(c => c.type === 'recipient').map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
            <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} className="date-input" />
            <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} className="date-input" />
            <input type="text" placeholder="Filter by #..." value={filterId} onChange={(e) => { setFilterId(e.target.value); setPage(1); }} className="filter-id-input" />
            <button onClick={() => { setFilter('all'); setFilterId(''); setFilterConcept(''); setFilterPayer(''); setFilterRecipient(''); setDateFrom(''); setDateTo(''); setPage(1); }} className="btn btn-secondary" title="Reset all filters">↺</button>
          </div>
        </div>

        <div className="totals-bar">
          <div className="total-item income"><span className="total-label">Income:</span><span className="total-value">{formatAmount(totals.income)}</span></div>
          <div className="total-item withdrawals"><span className="total-label">Withdrawals:</span><span className="total-value">{formatAmount(totals.withdrawals, 'withdrawal')}</span></div>
          <div className="total-item net"><span className="total-label">Net:</span><span className="total-value">{formatAmount(totals.net)}</span></div>
        </div>

        {loading ? <div className="loading"><div className="spinner"></div></div> : (!receipts || receipts.length === 0) ? (
          <div className="empty-state">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
            <h3>No receipts yet</h3><p>Create your first receipt</p>
            <button onClick={() => setShowModal(true)} className="btn btn-primary" style={{ marginTop: '1rem' }}>+ Create Receipt</button>
          </div>
        ) : (
          <>
            {/* Desktop table view */}
            <div className="receipts-table-wrapper hide-on-mobile">
            <table className="receipts-table">
              <thead>
                <tr>
                  <th className="status-col" title="Status"></th>
                  <th onClick={() => { setSort(sort === 'id_desc' ? 'id_asc' : 'id_desc'); setPage(1); }} className="sortable"># {sort === 'id_desc' ? '↓' : sort === 'id_asc' ? '↑' : ''}</th>
                  <th onClick={() => { setSort(sort === 'date_desc' ? 'date_asc' : 'date_desc'); setPage(1); }} className="sortable">Date {sort === 'date_desc' ? '↓' : sort === 'date_asc' ? '↑' : ''}</th>
                  <th onClick={() => { setSort(sort === 'amount_desc' ? 'amount_asc' : 'amount_desc'); setPage(1); }} className="sortable">Amount {sort === 'amount_desc' ? '↓' : sort === 'amount_asc' ? '↑' : ''}</th>
                  <th>Payer</th><th>Recipient</th><th>Method</th><th>Description</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {receipts.map((receipt) => {
                  const isCancelled = receipt.status === 'cancelled';
                  return (
                  <tr key={receipt.id} className={isCancelled ? 'cancelled' : ''}>
                    <td className="receipt-status-cell">
                      <span
                        className={`status-dot ${isCancelled ? 'status-dot--cancelled' : 'status-dot--valid'}`}
                        title={isCancelled ? 'Cancelled' : 'Valid'}
                        aria-label={isCancelled ? 'Cancelled' : 'Valid'}
                      >
                        {isCancelled ? '✗' : '✓'}
                      </span>
                      {(() => {
                        const resolved = resolveReceiptEmail(receipt);
                        if (!resolved) return null;
                        const fromContact = !receipt.payer_email;
                        return (
                          <span
                            className="receipt-email-marker"
                            title={`Emailable — payer email: ${resolved}${fromContact ? ' (resuelto del contacto)' : ''}`}
                            aria-label={`Has payer email (${resolved})`}
                          >
                            📧
                          </span>
                        );
                      })()}
                    </td>
                    <td className="receipt-id">#{receipt.id}</td>
                    <td>{formatDate(receipt.date)}</td>
                    <td className={`amount ${receipt.type}`}>{formatAmount(receipt.amount, receipt.type)}</td>
                    <td>{receipt.payer}</td><td>{receipt.recipient}</td>
                    <td><span className={`payment-method ${receipt.payment_method}`}>{receipt.payment_method}</span></td>
                    <td className="description" title={receipt.description}>{receipt.description ? receipt.description.slice(0, 20) + (receipt.description.length > 20 ? '...' : '') : '-'}</td>
                    <td className="receipts-actions">
                      <button onClick={() => navigate(`/receipts/${receipt.id}`)} className="btn btn-secondary btn-sm">View</button>
                      <button onClick={() => handleCancel(receipt.id)} className="btn btn-warning btn-sm">Cancel</button>
                      {(() => {
                        const resolved = resolveReceiptEmail(receipt);
                        if (!resolved) return null;
                        const fromContact = !receipt.payer_email;
                        return (
                          <button
                            onClick={() => receipt.status === 'valid' && handleSendEmail(receipt)}
                            disabled={sendingEmailId === receipt.id || receipt.status !== 'valid'}
                            className={`btn btn-email btn-sm${receipt.status !== 'valid' ? ' btn-email--cancelled' : ''}`}
                            title={
                              receipt.status === 'valid'
                                ? `Enviar PDF por email a ${resolved}${fromContact ? ' (del contacto)' : ''}`
                                : `Recibo cancelado — solo se pueden enviar recibos en estado válido (${resolved})`
                            }
                            aria-label={`Send receipt ${receipt.id} PDF by email to ${resolved}`}
                          >
                            {sendingEmailId === receipt.id ? '⏳ Enviando…' : '📧 Email'}
                          </button>
                        );
                      })()}
                      {user && user.role === 'system_admin' && (
                        <button
                          onClick={() => openDeleteModal(receipt.id)}
                          className="btn btn-danger btn-sm"
                          title="Permanently delete (system_admin only)"
                        >
                          <IconTrash size={14} />
                        </button>
                      )}
                      {rowMessages[receipt.id] && (
                        <span
                          className={`receipt-row-message receipt-row-message--${rowMessages[receipt.id].kind}`}
                          title={rowMessages[receipt.id].text}
                        >
                          {rowMessages[receipt.id].text}
                        </span>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
            
            {/* Mobile card view */}
            <div className="receipts-cards show-on-mobile">
              {receipts.map((receipt) => (
                <div key={receipt.id} className={`receipt-card ${receipt.type}${receipt.status === 'cancelled' ? ' cancelled' : ''}`}>
                  <div className="receipt-card-header">
                    <div className="receipt-card-id-wrap">
                      <span className="receipt-card-id">#{receipt.id}</span>
                      {receipt.status === 'cancelled' && (
                        <span className="cancelled-badge" aria-label="Cancelled">✗ CANCELLED</span>
                      )}
                      {(() => {
                        const resolved = resolveReceiptEmail(receipt);
                        if (!resolved) return null;
                        const fromContact = !receipt.payer_email;
                        return (
                          <span
                            className="receipt-card-email-marker"
                            title={`Emailable — payer email: ${resolved}${fromContact ? ' (resuelto del contacto)' : ''}`}
                            aria-label={`Has payer email (${resolved})`}
                          >
                            📧
                          </span>
                        );
                      })()}
                    </div>
                    <span className={`receipt-card-amount ${receipt.type}`}>{formatAmount(receipt.amount, receipt.type)}</span>
                  </div>
                  <div className="receipt-card-body">
                    <div className="receipt-card-row">
                      <span className="label">Date:</span>
                      <span>{formatDate(receipt.date)}</span>
                    </div>
                    <div className="receipt-card-row">
                      <span className="label">Payer:</span>
                      <span>{receipt.payer}</span>
                    </div>
                    <div className="receipt-card-row">
                      <span className="label">To:</span>
                      <span>{receipt.recipient}</span>
                    </div>
                    <div className="receipt-card-row">
                      <span className="label">Method:</span>
                      <span className={`payment-method ${receipt.payment_method}`}>{receipt.payment_method}</span>
                    </div>
                    <div className="receipt-card-row">
                      <span className="label">Description:</span>
                      <span className="description" title={receipt.description}>{receipt.description ? receipt.description.slice(0, 20) + (receipt.description.length > 20 ? '...' : '') : '-'}</span>
                    </div>
                  </div>
                  <div className="receipt-card-actions">
                    <button onClick={() => navigate(`/receipts/${receipt.id}`)} className="btn btn-secondary btn-sm">View</button>
                    <button onClick={() => handleCancel(receipt.id)} className="btn btn-warning btn-sm">Cancel</button>
                    {(() => {
                      const resolved = resolveReceiptEmail(receipt);
                      if (!resolved) return null;
                      const fromContact = !receipt.payer_email;
                      return (
                        <button
                          onClick={() => receipt.status === 'valid' && handleSendEmail(receipt)}
                          disabled={sendingEmailId === receipt.id || receipt.status !== 'valid'}
                          className={`btn btn-email btn-sm${receipt.status !== 'valid' ? ' btn-email--cancelled' : ''}`}
                          title={
                            receipt.status === 'valid'
                              ? `Enviar PDF por email a ${resolved}${fromContact ? ' (del contacto)' : ''}`
                              : `Recibo cancelado — solo se pueden enviar recibos en estado válido (${resolved})`
                          }
                          aria-label={`Send receipt ${receipt.id} PDF by email to ${resolved}`}
                        >
                          {sendingEmailId === receipt.id ? '⏳' : '📧 Email'}
                        </button>
                      );
                    })()}
                    {user && user.role === 'system_admin' && (
                      <button
                        onClick={() => openDeleteModal(receipt.id)}
                        className="btn btn-danger btn-sm"
                        title="Permanently delete (system_admin only)"
                      >
                        <IconTrash size={14} />
                      </button>
                    )}
                    {rowMessages[receipt.id] && (
                      <span
                        className={`receipt-card-message receipt-card-message--${rowMessages[receipt.id].kind}`}
                      >
                        {rowMessages[receipt.id].text}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            {totalPages > 1 && (
              <div className="pagination">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Previous</button>
                <span className="page-info">Page {page} of {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next</button>
              </div>
            )}

            {/* Page-size selector — always visible so a single-page list can
                still switch from 10 to 100 without having to reload first. */}
            <div className="pagination pagination-page-size">
              <label htmlFor="dashboard-page-size" className="page-size-label">
                Per page:
              </label>
              <select
                id="dashboard-page-size"
                value={limit}
                onChange={(e) => handleLimitChange(parseInt(e.target.value, 10))}
                className="page-size-select"
              >
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
              <span className="page-size-total">
                ({total} receipt{total === 1 ? '' : 's'})
              </span>
            </div>
          </>
        )}
      </div>

      {/* New Receipt Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h2>New Receipt</h2><button className="modal-close" onClick={() => setShowModal(false)}>×</button></div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {error && <div className="alert alert-error">{error}</div>}
                <div className="form-group">
                  <label>Amount ($)</label>
                  <input type="number" name="amount" value={formData.amount} onChange={(e) => setFormData({...formData, amount: e.target.value})} step="0.01" min="0.01" required />
                </div>
                <div className="form-group">
                  <label>Payer</label>
                  <div className="select-with-add">
                    <select value={newPayer ? '' : formData.payer} onChange={(e) => { setFormData({...formData, payer: e.target.value}); setNewPayer(''); }}>
                      <option value="">Select...</option>
                      {contactsList.filter(c => c.type === 'sender').map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                    </select>
                    <input type="text" placeholder="Or type new..." value={newPayer} onChange={(e) => { setNewPayer(e.target.value); setFormData({...formData, payer: e.target.value}); }} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Recipient</label>
                  <div className="select-with-add">
                    <select value={newRecipient ? '' : formData.recipient} onChange={(e) => { setFormData({...formData, recipient: e.target.value}); setNewRecipient(''); }}>
                      <option value="">Select...</option>
                      {contactsList.filter(c => c.type === 'recipient').map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                    </select>
                    <input type="text" placeholder="Or type new..." value={newRecipient} onChange={(e) => { setNewRecipient(e.target.value); setFormData({...formData, recipient: e.target.value}); }} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Date</label>
                  <input type="datetime-local" name="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} required />
                </div>
                <div className="form-group">
                  <label>Concept</label>
                  <div className="select-with-add">
                    <select value={newConcept ? '' : formData.concept} onChange={(e) => { setFormData({...formData, concept: e.target.value}); setNewConcept(''); }}>
                      <option value="">Select...</option>
                      {contactsList.filter(c => c.type === 'concept').map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                    </select>
                    <input type="text" placeholder="Or type new..." value={newConcept} onChange={(e) => { setNewConcept(e.target.value); setFormData({...formData, concept: e.target.value}); }} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Payment Method</label>
                  <select name="payment_method" value={formData.payment_method} onChange={(e) => setFormData({...formData, payment_method: e.target.value})}>
                    <option value="cash">Cash</option><option value="electronic">Electronic Transfer</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Creating...' : 'Create Receipt'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New Withdrawal Modal */}
      {showWithdrawalModal && (
        <div className="modal-overlay" onClick={() => { setShowWithdrawalModal(false); setNewWFrom(''); setNewWTo(''); setNewWConcept(''); }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h2>New Withdrawal / Payment</h2><button className="modal-close" onClick={() => { setShowWithdrawalModal(false); setNewWFrom(''); setNewWTo(''); setNewWConcept(''); }}>×</button></div>
            <form onSubmit={handleSubmitWithdrawal}>
              <div className="modal-body">
                {error && <div className="alert alert-error">{error}</div>}
                <div className="form-group">
                  <label>Amount ($)</label>
                  <input type="number" step="0.01" min="0.01" value={withdrawalData.amount} onChange={(e) => setWithdrawalData({...withdrawalData, amount: e.target.value})} required />
                </div>
                <div className="form-group">
                  <label>From (Payer)</label>
                  <div className="select-with-add">
                    <select value={newWFrom ? '' : withdrawalData.payer} onChange={(e) => { setWithdrawalData({...withdrawalData, payer: e.target.value}); setNewWFrom(''); }}>
                      <option value="">Select...</option>
                      {contactsList.filter(c => c.type === 'sender').map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                    </select>
                    <input type="text" placeholder="Or type new..." value={newWFrom} onChange={(e) => { setNewWFrom(e.target.value); setWithdrawalData({...withdrawalData, payer: e.target.value}); }} />
                  </div>
                </div>
                <div className="form-group">
                  <label>To (Recipient)</label>
                  <div className="select-with-add">
                    <select value={newWTo ? '' : withdrawalData.recipient} onChange={(e) => { setWithdrawalData({...withdrawalData, recipient: e.target.value}); setNewWTo(''); }}>
                      <option value="">Select...</option>
                      {contactsList.filter(c => c.type === 'recipient').map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                    </select>
                    <input type="text" placeholder="Or type new..." value={newWTo} onChange={(e) => { setNewWTo(e.target.value); setWithdrawalData({...withdrawalData, recipient: e.target.value}); }} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Date</label>
                  <input type="datetime-local" value={withdrawalData.date} onChange={(e) => setWithdrawalData({...withdrawalData, date: e.target.value})} required />
                </div>
                <div className="form-group">
                  <label>Concept</label>
                  <div className="select-with-add">
                    <select value={newWConcept ? '' : withdrawalData.concept} onChange={(e) => { setWithdrawalData({...withdrawalData, concept: e.target.value}); setNewWConcept(''); }}>
                      <option value="">Select...</option>
                      {contactsList.filter(c => c.type === 'concept').map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                    </select>
                    <input type="text" placeholder="Or type new..." value={newWConcept} onChange={(e) => { setNewWConcept(e.target.value); setWithdrawalData({...withdrawalData, concept: e.target.value}); }} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea value={withdrawalData.description} onChange={(e) => setWithdrawalData({...withdrawalData, description: e.target.value})} rows="3" />
                </div>
                <div className="form-group">
                  <label>Payment Method</label>
                  <select value={withdrawalData.payment_method} onChange={(e) => setWithdrawalData({...withdrawalData, payment_method: e.target.value})}>
                    <option value="cash">Cash</option>
                    <option value="electronic">Electronic Transfer</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => { setShowWithdrawalModal(false); setNewWFrom(''); setNewWTo(''); setNewWConcept(''); }}>Cancel</button>
                <button type="submit" className="btn btn-warning" disabled={submitting}>{submitting ? 'Creating...' : 'Create Withdrawal'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Quote Modal */}
      {showQuoteModal && (
        <div className="modal-overlay" onClick={() => setShowQuoteModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h2>New Quote</h2><button className="modal-close" onClick={() => setShowQuoteModal(false)}>×</button></div>
            <form onSubmit={handleSubmitQuote}>
              <div className="form-group"><label>Quote Number</label><input type="text" value={quoteData.quote_number} onChange={(e) => setQuoteData({...quoteData, quote_number: e.target.value})} placeholder="Q-001" required /></div>
              <div className="form-group"><label>Type</label><select value={quoteData.quote_type || ''} onChange={(e) => setQuoteData({...quoteData, quote_type: e.target.value})}><option value="">Select type...</option><option value="service">Service</option><option value="product">Product</option><option value="consulting">Consulting</option><option value="maintenance">Maintenance</option><option value="other">Other</option></select></div>
              <div className="form-group"><label>Client</label><input type="text" value={quoteData.client} onChange={(e) => setQuoteData({...quoteData, client: e.target.value})} placeholder="Who pays us" required /></div>
              <div className="form-group"><label>Provider</label><input type="text" value={quoteData.provider} onChange={(e) => setQuoteData({...quoteData, provider: e.target.value})} placeholder="Who provides the service/product" required /></div>
              <div className="form-group"><label>Amount</label><input type="number" step="0.01" min="0.01" value={quoteData.amount} onChange={(e) => setQuoteData({...quoteData, amount: e.target.value})} required /></div>
              <div className="form-group"><label>Date</label><input type="date" value={quoteData.date} onChange={(e) => setQuoteData({...quoteData, date: e.target.value})} required /></div>
              <div className="form-group"><label>Description</label><textarea value={quoteData.description} onChange={(e) => setQuoteData({...quoteData, description: e.target.value})} rows="3" /></div>
              <div className="form-group">
                <label>Photo Evidence</label>
                <input type="file" accept="image/*" multiple onChange={(e) => { const files = Array.from(e.target.files); setQuotePhotoFiles(files); const previews = files.map(f => URL.createObjectURL(f)); setQuotePhotos(previews); setQuoteCropData({}); }} />
                {quotePhotos.length > 0 && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                      {quotePhotos.map((src, idx) => (
                        <div key={idx} style={{ position: 'relative' }}>
                          <img src={src} alt="Preview" style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '4px', filter: quoteGrayscale ? 'grayscale(100%)' : 'none', border: quoteCropData[idx] ? '2px solid #28a745' : 'none' }} />
                          <button type="button" onClick={() => { setCropImageIndex(idx); setShowCropModal(true); }} style={{ position: 'absolute', bottom: '2px', right: '2px', background: 'rgba(0,0,0,0.7)', color: '#fff', border: 'none', borderRadius: '2px', cursor: 'pointer', fontSize: '10px', padding: '2px 4px' }}>✂</button>
                          <button type="button" onClick={() => { const newFiles = quotePhotoFiles.filter((_, i) => i !== idx); const newPreviews = quotePhotos.filter((_, i) => i !== idx); setQuotePhotoFiles(newFiles); setQuotePhotos(newPreviews); setQuoteCropData(prev => { const n = { ...prev }; delete n[idx]; return n; }); }} style={{ position: 'absolute', top: '2px', right: '2px', background: 'rgba(220,53,69,0.9)', color: '#fff', border: 'none', borderRadius: '50%', cursor: 'pointer', width: '18px', height: '18px', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                          {quoteCropData[idx] && <span style={{ position: 'absolute', top: '2px', left: '2px', background: '#28a745', color: '#fff', borderRadius: '50%', width: '16px', height: '16px', fontSize: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✓</span>}
                        </div>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <input type="checkbox" checked={quoteGrayscale} onChange={(e) => setQuoteGrayscale(e.target.checked)} />
                        Grayscale
                      </label>
                      <label>Quality:
                        <select value={quoteCompression} onChange={(e) => setQuoteCompression(parseFloat(e.target.value))}>
                          <option value="1.0">100%</option>
                          <option value="0.8">80%</option>
                          <option value="0.6">60%</option>
                          <option value="0.4">40%</option>
                        </select>
                      </label>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowQuoteModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-success" disabled={quoteSubmitting}>{quoteSubmitting ? 'Creating...' : 'Create Quote'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>⚠️ Cancel Receipt</h2><p>Reason for cancellation:</p>
            <div className="form-group"><textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="Enter reason..." rows="3" required /></div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowCancelModal(false)}>Keep Receipt</button>
              <button type="button" className="btn btn-warning" onClick={handleConfirmCancel}>Confirm Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Admin hard-delete confirmation modal — requires typing the receipt ID. */}
      {deleteTargetId !== null && (
        <div className="modal-overlay" onClick={closeDeleteModal}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
            <div className="modal-header">
              <h2 style={{ color: 'var(--danger, #991b1b)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><IconTrash size={20} /> Delete receipt #{deleteTargetId}?</h2>
              <button className="modal-close" onClick={closeDeleteModal}>×</button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); handleDelete(); }}>
              <div className="modal-body">
                {deleteError && <div className="alert alert-error">{deleteError}</div>}
                <p style={{ marginTop: 0 }}>
                  This will <strong>permanently</strong> delete receipt <strong>#{deleteTargetId}</strong>.
                  Cancel is reversible (the receipt stays in the database). Delete is not — once
                  confirmed, the receipt and its comments are removed from the database immediately.
                  An audit-log entry is written before deletion.
                </p>
                <div className="form-group">
                  <label>Type <code>{deleteTargetId}</code> to confirm:</label>
                  <input
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder={String(deleteTargetId)}
                    autoFocus
                    disabled={deleting}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeDeleteModal} disabled={deleting}>Cancel</button>
                <button
                  type="submit"
                  className="btn btn-danger"
                  disabled={deleting || deleteConfirmText !== String(deleteTargetId)}
                >
                  {deleting ? 'Deleting…' : 'Delete permanently'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div className="modal-overlay" onClick={() => setShowExportModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📄 Export Report</h2>
              <button className="modal-close" onClick={() => setShowExportModal(false)} aria-label="Close">×</button>
            </div>
            <div className="modal-body">
              <p className="modal-intro">Select the report layout to download.</p>
              <div className="form-group">
                <label htmlFor="report-type">Report Type</label>
                <select id="report-type" value={reportType} onChange={(e) => setReportType(e.target.value)} className="form-select">
                  <option value="regular">Regular Mode — Standard list</option>
                  <option value="pooled">Pooled Funds — Matrix (Payers × Concepts)</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowExportModal(false)}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={handleProceedExport}>Proceed</button>
            </div>
          </div>
        </div>
      )}

      {/* Concept Selection Modal */}
      {showConceptModal && (
        <div className="modal-overlay" onClick={() => setShowConceptModal(false)}>
          <div className="modal concept-select-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📊 Select Concepts</h2>
              <button className="modal-close" onClick={() => { setShowConceptModal(false); setConceptPage(1); }} aria-label="Close">×</button>
            </div>
            <div className="modal-body">
              {concepts.length === 0 ? (
                <p className="modal-empty">No concepts found.</p>
              ) : (
                <>
                  <div className="concept-table-wrap">
                    <table className="data-table">
                      <thead><tr><th style={{ width: '50px', textAlign: 'center' }}>✓</th><th>Concept</th></tr></thead>
                      <tbody>
                        {concepts.slice((conceptPage - 1) * conceptsPerPage, conceptPage * conceptsPerPage).map(concept => (
                          <tr key={concept}>
                            <td style={{ textAlign: 'center' }}><input type="checkbox" checked={selectedConcepts.includes(concept)} onChange={(e) => { if (e.target.checked) setSelectedConcepts([...selectedConcepts, concept]); else setSelectedConcepts(selectedConcepts.filter(c => c !== concept)); }} style={{ width: '18px', height: '18px' }} /></td>
                            <td>{concept}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {concepts.length > conceptsPerPage && (
                    <div className="modal-pagination">
                      <button onClick={() => setConceptPage(p => Math.max(1, p - 1))} disabled={conceptPage === 1} className="btn btn-secondary">← Prev</button>
                      <span>Page {conceptPage} of {Math.ceil(concepts.length / conceptsPerPage)}</span>
                      <button onClick={() => setConceptPage(p => Math.min(Math.ceil(concepts.length / conceptsPerPage), p + 1))} disabled={conceptPage >= Math.ceil(concepts.length / conceptsPerPage)} className="btn btn-secondary">Next →</button>
                    </div>
                  )}
                  <div className="modal-counter">
                    <span>Selected: {selectedConcepts.length} / {concepts.length}</span>
                    <span className="modal-counter-actions">
                      <button onClick={() => setSelectedConcepts([...concepts])} className="link-button">All</button>
                      <button onClick={() => setSelectedConcepts([])} className="link-button">None</button>
                    </span>
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => { setShowConceptModal(false); setConceptPage(1); }}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={handlePooledExport} disabled={selectedConcepts.length === 0}>Generate</button>
            </div>
          </div>
        </div>
      )}

      {/* Crop Modal */}
      {showCropModal && cropImageIndex !== null && (
        <div className="modal-overlay" onClick={() => setShowCropModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px', width: '90%' }}>
            <div className="modal-header">
              <h2>Crop Photo</h2>
              <button className="modal-close" onClick={() => setShowCropModal(false)}>×</button>
            </div>
            <div style={{ padding: '1rem' }}>
              <ReactCrop
                crop={crop}
                onChange={c => setCrop(c)}
                onComplete={c => setCompletedCrop(c)}
                aspect={crop.aspect}
              >
                <img
                  ref={imgRef}
                  src={quotePhotos[cropImageIndex]}
                  alt="Crop preview"
                  style={{ maxWidth: '100%', maxHeight: '400px' }}
                />
              </ReactCrop>
              <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button className="btn btn-outline" onClick={() => { setCrop({ ...crop, aspect: undefined }); }}>Free</button>
                <button className="btn btn-outline" onClick={() => { setCrop({ ...crop, aspect: 1 }); }}>1:1</button>
                <button className="btn btn-outline" onClick={() => { setCrop({ ...crop, aspect: 4/3 }); }}>4:3</button>
                <button className="btn btn-outline" onClick={() => { setCrop({ ...crop, aspect: 16/9 }); }}>16:9</button>
              </div>
              <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button className="btn btn-outline" onClick={() => { setQuoteCropData(prev => { const n = { ...prev }; delete n[cropImageIndex]; return n; }); setShowCropModal(false); }}>Reset Crop</button>
                <button className="btn btn-primary" onClick={() => {
                  if (completedCrop && imgRef.current) {
                    const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
                    const scaleY = imgRef.current.naturalHeight / imgRef.current.height;
                    const newCropData = {
                      x: completedCrop.x * scaleX,
                      y: completedCrop.y * scaleY,
                      width: completedCrop.width * scaleX,
                      height: completedCrop.height * scaleY
                    };
                    setQuoteCropData(prev => ({ ...prev, [cropImageIndex]: newCropData }));
                  }
                  setShowCropModal(false);
                }}>Apply Crop</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
